import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Container } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Chip from "@mui/material/Chip";

import dayjs from "dayjs";
import isEmpty from "lodash-es/isEmpty";

import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";

import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import { ReactComponent as DeleteIcon } from "../../icons/Delete24.svg";

import Alert from "@mui/material/Alert";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";
import SelectStudentsDialog from "../../components/faculty/SelectStudentsDialog";
import SelectStudentListsDialog from "../../components/faculty/SelectStudentListsDialog";
import AddStudentResultDialog from "../../components/faculty/AddStudentResultDialog";

import { doService } from "../../services/ServiceHelper";
import { showError } from "../../components/ATPDialogs";
import TableRowNoResults from "../../components/TableRowNoResults";
import SearchBar from "../../components/faculty/SearchBar";

import {
  getSchoolStudents,
  getTeacherUserLists,
  getUserListMembers,
} from "../../services/UserService";
import { getPeriodName } from "../../services/SchoolService";

import { bulkLookup, bulkAdd } from "../../services/ActivityService";

import remove from "lodash-es/remove";
import SubTitle from "../../components/SubTitle";
import MyActivitySelector from "../../components/faculty/report/MyActivitySelector";

import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";

const BulkChange = () => {
  const navigate = useNavigate();

  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();
  const [showProgress, setShowProgress] = useState(false);

  const [_activeStep, setActiveStep] = React.useState(0);
  const [_dateRange, setDateRange] = useState([dayjs(), dayjs()]);

  const STEPS = [
    "Select Students",
    "Look Up Activity Dates",
    "Verify and Add",
  ];

  const [_dialogStudentsOpen, setDialogStudentsOpen] = useState(false);
  const [_dialogStudentListsOpen, setDialogStudentListsOpen] =
    React.useState(false);
  const [_dialogStudentResultOpen, setDialogStudenResultOpen] =
    React.useState(false);

  const [_allStudents, setAllStudents] = useState([]);
  const [_selectedStudents, setSelectedStudents] = useState([]);
  const [_userLists, setUserLists] = useState([]);

  const [_selectedPeriod, setSelectedPeriod] = React.useState("1");
  const [_selectedActivityEid, setSelectedActivityEid] = useState("XX");

  const [_selectedActivityDates, setSelectedActivityDates] = useState([]);

  const [_mondayChecked, setMondayChecked] = useState(true);
  const [_tuesdayChecked, setTuesdayChecked] = useState(true);
  const [_wednesdayChecked, setWednesdayChecked] = useState(true);
  const [_thursdayChecked, setThursdayChecked] = useState(true);
  const [_fridayChecked, setFridayChecked] = useState(true);

  const [_studentCannotModify, setStudentCannotModify] = useState(false);
  const [_teacherCannotModify, setTeacherCannotModify] = useState(false);

  const [_addSuccesses, setAddSuccesses] = useState([]);
  const [_addFails, setAddFails] = useState([]);
  const [_isFinishDisabled, setFinishDisabled] = useState(false);

  useEffect(() => {
    initStudents();
    initUserLists();
  }, []);

  const initUserLists = () => {
    doService(getTeacherUserLists(_school, _jwt), (results) => {
      const labeledLists = [];
      for (const studentList of results.data) {
        labeledLists.push({
          label: studentList.name,
          value: studentList.id,
        });
      }
      setUserLists(labeledLists);
    });
  };

  const initStudents = () => {
    doService(getSchoolStudents(_school, _jwt), (results) => {
      const labeledStudents = [];
      for (const student of results.data) {
        let label = `${student.last_name}, ${student.first_name} (${student.grade})`;

        if (!isEmpty(student.externalId)) {
          label += ` [${student.externalId}]`;
        }

        labeledStudents.push({
          label: label,
          value: student.eid,
        });
      }

      setAllStudents(labeledStudents);
    });
  };

  const handleStep = (stepNum) => () => {
    if (!validateStep(_activeStep)) {
      return;
    }

    setActiveStep(stepNum);
  };

  const totalSteps = () => {
    return STEPS.length;
  };

  const isLastStep = () => {
    return _activeStep === totalSteps() - 1;
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = (stepNum) => {
    if (stepNum === 0) {
      // make sure there's 1+ students selected
      if (_selectedStudents.length === 0) {
        showError("Please select 1 or more students.");
        return false;
      }
    } else if (stepNum === 1) {
      // make sure there's 1+ dates selected
      if (_selectedActivityDates.length === 0) {
        showError("Please select 1 or more activity dates.");
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    const newActiveStep = _activeStep + 1;

    // validate current step
    if (!validateStep(_activeStep)) {
      return;
    }
    setActiveStep(newActiveStep);
  };

  const getStudentEids = () => {
    const eids = [];

    for (let student of _selectedStudents) {
      eids.push(student.value);
    }
    return eids;
  };

  const getActivityDateEids = () => {
    const eids = [];

    for (let ad of _selectedActivityDates) {
      eids.push(ad.eid);
    }
    return eids;
  };

  const handleComplete = () => {
    const studentEids = getStudentEids();
    const activityDateEids = getActivityDateEids();

    const studentModifyStr = _studentCannotModify ? "N" : "Y";
    const teacherModifyStr = _teacherCannotModify ? "N" : "Y";
    setFinishDisabled(true);

    //
    const currentCountToAdd = _selectedStudents.length;

    let messages = "";
    for (let activityDate of _selectedActivityDates) {
      const maxCount = activityDate.maxAttendance;
      const proposedCount = activityDate.signupCount + currentCountToAdd;

      if (proposedCount > maxCount) {
        messages += dayjs(activityDate.date).format("MM/DD") + "\n";
      }
    }

    if (messages.length > 0) {
      let allMessages = `WARNING: If you add this set of student(s), you will exceed the activity's max attendance on the specified date(s).\nClick OK to continue adding students, or cancel to stop.\n\nThe activity dates that will exceed max attendance are:\n\n${messages}`;
      if (!window.confirm(allMessages)) {
        return;
      }
    }

    doService(
      bulkAdd(
        _school,
        _jwt,
        studentEids,
        activityDateEids,
        studentModifyStr,
        teacherModifyStr,
      ),
      (results) => {
        setShowProgress(false);
        setFinishDisabled(false);

        setAddSuccesses(results.data.successes);
        setAddFails(results.data.fails);
        setDialogStudenResultOpen(true);
      },
    );
  };

  const handleStudentsClose = () => {
    setDialogStudentsOpen(false);
  };

  const handleStudentsOK = (selectedStudents) => {
    setDialogStudentsOpen(false);

    if (selectedStudents.length === 0) {
      return;
    }

    // guarantee no duplicates
    // map: eid
    const oldSelectedStudentsMap = getExistingStudentsMap();

    // collect IDs from raw values
    const newSelectedStudents = [..._selectedStudents];
    for (const student of selectedStudents) {
      if (oldSelectedStudentsMap[student.value] != null) {
        // skip students already in existing list
        continue;
      }

      newSelectedStudents.push(student);
    }
    setSelectedStudents(newSelectedStudents);
  };

  const getExistingStudentsMap = () => {
    // guarantee no duplicates
    // map: eid
    const oldSelectedStudentsMap = {};
    for (let oldSelectedStudent of _selectedStudents) {
      oldSelectedStudentsMap[oldSelectedStudent.value] = oldSelectedStudent;
    }
    return oldSelectedStudentsMap;
  };

  const handleClickSelectStudents = () => {
    setDialogStudentsOpen(true);
  };

  const handleClickSelectStudentList = () => {
    setDialogStudentListsOpen(true);
  };

  const handleClickRemoveStudent = (student) => {
    const newSelectedStudents = [..._selectedStudents];
    remove(newSelectedStudents, student);
    setSelectedStudents(newSelectedStudents);
  };

  const handleStudentListClose = () => {
    setDialogStudentListsOpen(false);
  };

  const handleStudentListOK = (selectedList) => {
    setDialogStudentListsOpen(false);

    setShowProgress(true);
    doService(
      getUserListMembers(_school, _jwt, selectedList.value),
      (results) => {
        // guarantee no duplicates
        // map: eid
        const oldSelectedStudentsMap = getExistingStudentsMap();

        const newSelectedStudents = [..._selectedStudents];

        for (const entry of results.data) {
          if (oldSelectedStudentsMap[entry.eid] == null) {
            // add to selected entry
            newSelectedStudents.push({
              label:
                entry.last_name +
                ", " +
                entry.first_name +
                " (" +
                entry.grade +
                ")",
              value: entry.eid,
            });
          }
        }

        setSelectedStudents(newSelectedStudents);
        setShowProgress(false);
      },
    );
  };

  const handleChangeDateRange = (value) => setDateRange(value);

  const handleChangePeriod = (event) => {
    setSelectedPeriod(event.target.value);
    // if (onChangePeriod) {
    //   onChangePeriod(event.target.value);
    // }
  };

  const onChangeActivityEid = (activityEid) => {
    setSelectedActivityEid(activityEid);
  };

  const handleChangeDate = (event, day) => {
    switch (day) {
      case "M":
        setMondayChecked(event.target.checked);
        break;
      case "T":
        setTuesdayChecked(event.target.checked);
        break;
      case "W":
        setWednesdayChecked(event.target.checked);
        break;
      case "R":
        setThursdayChecked(event.target.checked);
        break;
      case "F":
        setFridayChecked(event.target.checked);
        break;
    }
  };

  const allDatesDeselected = () => {
    return (
      !_mondayChecked &&
      !_tuesdayChecked &&
      !_wednesdayChecked &&
      !_thursdayChecked &&
      !_fridayChecked
    );
  };

  const getDays = () => {
    let days = "";

    if (_mondayChecked) days += "M";
    if (_tuesdayChecked) days += "T";
    if (_wednesdayChecked) days += "W";
    if (_thursdayChecked) days += "R";
    if (_fridayChecked) days += "F";
    return days;
  };

  const handleLookupActivityDates = () => {
    if (_selectedActivityEid === "XX") {
      showError("Unable to Look Up Activities", "Please select an Activity.");
      return;
    }

    if (allDatesDeselected()) {
      showError(
        "Unable to Look Up Activities",
        "Please select 1 or more days.",
      );
      return;
    }

    const dateStart = _dateRange[0].format("YYYY-MM-DD");
    const dateEnd = _dateRange[1].format("YYYY-MM-DD");

    setShowProgress(true);
    doService(
      bulkLookup(
        _school,
        _jwt,
        _selectedActivityEid,
        dateStart,
        dateEnd,
        _selectedPeriod,
        getDays(),
      ),
      (results) => {
        setSelectedActivityDates(results.data);
        setShowProgress(false);
      },
    );
  };

  const handleClickRemoveDate = (date) => {
    const newSelectedActivityDates = [..._selectedActivityDates];
    remove(newSelectedActivityDates, date);
    setSelectedActivityDates(newSelectedActivityDates);
  };

  const getActivityName = () => {
    // retrieves from first item
    if (_selectedActivityDates.length === 0) {
      return "-- NONE --";
    }
    return `${_selectedActivityDates[0].name} [${_selectedActivityDates[0].last_name}]`;
  };

  const handleChangeStudentCannotModify = (event) => {
    setStudentCannotModify(event.target.checked);
  };

  const handleChangeTeacherCannotModify = (event) => {
    setTeacherCannotModify(event.target.checked);
  };

  const handleStudentResultClose = () => {
    setDialogStudenResultOpen(false);
    navigate("/teacher/welcome");
  };

  return (
    <>
      <DrawerAppBar />
      <SearchBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Student Bulk Activity Change" showBack={true} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info">
              This tool allows you to add multiple students to multiple
              activities, for a specified date range.
            </Alert>
          </Grid>
          <Grid item xs={6}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              You MUST add Activities to your Calendar before you use this tool
              to add students.
            </Alert>
          </Grid>
          <Grid item xs={6}>
            <Alert severity="warning">
              Please only use for 2 weeks at a time, as this process can take a
              while to run.
            </Alert>
          </Grid>
        </Grid>

        <ProgressBar showProgress={showProgress} />

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={3} sx={{ display: "flex" }}>
            <Paper
              sx={{
                alignItems: "flex-start",
                display: "flex",
                height: "100%",
                justifyContent: { xs: "flex-start", md: "center" },
                p: 1,
                width: "100%",
              }}
            >
              <Stepper
                nonLinear
                activeStep={_activeStep}
                orientation="vertical"
              >
                {STEPS.map((label, index) => (
                  <Step key={label}>
                    <StepButton color="inherit" onClick={handleStep(index)}>
                      {label}
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          </Grid>
          <Grid item xs={12} md={9}>
            <Box sx={{ boxShadow: 1, p: 1 }}>
              <SubTitle
                text={"Step " + (_activeStep + 1) + ": " + STEPS[_activeStep]}
              />

              {_activeStep === 0 && (
                <>
                  <Alert severity="info">
                    Enter a Student's Last/First Name, or select the Student
                    List and click the "Load Student List" button.
                    <br />
                    The identified students will be displayed in the Selected
                    Students section.
                  </Alert>
                  <Grid container spacing={2} sx={{ mt: 1.0, ml: 1 }}>
                    <Grid item xs={6} sx={{ borderRight: 1 }}>
                      <InputLabel>1.1. Select Students:</InputLabel>

                      <Button
                        variant="contained"
                        onClick={handleClickSelectStudents}
                        sx={{ mt: 1 }}
                      >
                        Select Individual(s)
                      </Button>
                      <Typography sx={{ mt: 1, mb: 1 }}>OR...</Typography>
                      <Button
                        variant="contained"
                        onClick={handleClickSelectStudentList}
                        className="button2"
                      >
                        Select Student List
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel>
                        1.2. Selected Students ({_selectedStudents.length}):
                      </InputLabel>

                      <List
                        dense
                        sx={{
                          overflow: "auto",
                          maxHeight: 300,
                          mr: 2,
                        }}
                      >
                        {_selectedStudents.map((student, index) => (
                          <ListItem
                            secondaryAction={
                              <IconButton
                                edge="end"
                                aria-label="delete student"
                                onClick={() =>
                                  handleClickRemoveStudent(student)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={index + 1 + ". " + student.label}
                            />
                          </ListItem>
                        ))}

                        {_selectedStudents.length === 0 && (
                          <>-- NO STUDENTS SELECTED --</>
                        )}
                      </List>
                    </Grid>
                  </Grid>
                </>
              )}

              {_activeStep === 1 && (
                <>
                  <Alert severity="info">
                    Select the Start/End Dates, the Period, and the Activity.
                    Click the "Look Up Activity Dates" to find the activities
                    you want to add - they will be displayed in the "Activity
                    Dates in Range" section on the right.
                  </Alert>

                  <Grid container spacing={2} sx={{ mt: 1.0, ml: 0 }}>
                    <Grid item xs={6} sx={{ borderRight: 1 }}>
                      <InputLabel>2.1. Start/End Date:</InputLabel>
                      <MobileDateRangePicker
                        defaultValue={[dayjs(), dayjs()]}
                        value={_dateRange}
                        sx={{ mt: 1, mb: 2, mr: 2 }}
                        onChange={handleChangeDateRange}
                      />

                      <Grid container sx={{ mt: 0.0, ml: 0 }}>
                        <Grid item xs={6}>
                          <InputLabel>2.2. Period:</InputLabel>

                          <Select
                            sx={{ mb: 1, width: "200px" }}
                            fullWidth
                            size="small"
                            onChange={handleChangePeriod}
                            value={_selectedPeriod}
                          >
                            {[...Array(_school.maxActivityCount)].map(
                              (x, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                  {getPeriodName(_school, i)}
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </Grid>
                        <Grid item xs={12} sx={{ mr: 2 }}>
                          <InputLabel>2.3. Activity:</InputLabel>
                          <MyActivitySelector
                            school={_school}
                            jwt={_jwt}
                            onChange={onChangeActivityEid}
                            showLabel={false}
                            selectLabel="-- SELECT --"
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ mr: 2 }}>
                          <InputLabel sx={{ mt: 1 }}>
                            2.4. Days in Range:
                          </InputLabel>

                          <Table
                            dense
                            sx={{ mt: 0, border: 0, mb: 0 }}
                            size="small"
                          >
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ mt: 0, pt: 0, border: 0 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        sx={{ ml: 0, mr: 1, pl: 0, pr: 0 }}
                                        onChange={(event) =>
                                          handleChangeDate(event, "M")
                                        }
                                        checked={_mondayChecked}
                                      />
                                    }
                                    label="M"
                                  />
                                </TableCell>
                                <TableCell sx={{ mt: 0, pt: 0, border: 0 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        sx={{ ml: 0, mr: 1, pl: 0, pr: 0 }}
                                        onChange={(event) =>
                                          handleChangeDate(event, "T")
                                        }
                                        checked={_tuesdayChecked}
                                      />
                                    }
                                    label="T"
                                  />
                                </TableCell>
                                <TableCell sx={{ mt: 0, pt: 0, border: 0 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        sx={{ ml: 0, mr: 1, pl: 0, pr: 0 }}
                                        onChange={(event) =>
                                          handleChangeDate(event, "W")
                                        }
                                        checked={_wednesdayChecked}
                                      />
                                    }
                                    label="W"
                                  />
                                </TableCell>{" "}
                                <TableCell sx={{ mt: 0, pt: 0, border: 0 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        sx={{ ml: 0, mr: 1, pl: 0, pr: 0 }}
                                        onChange={(event) =>
                                          handleChangeDate(event, "R")
                                        }
                                        checked={_thursdayChecked}
                                      />
                                    }
                                    label="R"
                                  />
                                </TableCell>
                                <TableCell sx={{ mt: 0, pt: 0, border: 0 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        sx={{ ml: 0, mr: 1, pl: 0, pr: 0 }}
                                        onChange={(event) =>
                                          handleChangeDate(event, "F")
                                        }
                                        checked={_fridayChecked}
                                      />
                                    }
                                    label="F"
                                  />
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Grid>
                      </Grid>

                      <Button
                        variant="contained"
                        disabled={_activeStep === 0}
                        onClick={handleLookupActivityDates}
                        sx={{ mr: 1 }}
                      >
                        2.5. Look Up Activity Dates
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel>
                        Activity Dates in Range ({_selectedActivityDates.length}
                        ):
                      </InputLabel>

                      <TableContainer component={Paper} sx={{ mt: 1, mb: 1 }}>
                        <Table dense sx={{ mt: 0, pr: 2 }} size="small">
                          <TableHead>
                            <StripedTableRow>
                              <StripedTableCell>Date</StripedTableCell>
                              <StripedTableCell>Period</StripedTableCell>
                              <StripedTableCell align="center">
                                Remove?
                              </StripedTableCell>
                            </StripedTableRow>
                          </TableHead>
                          <TableBody>
                            <TableRowNoResults data={_selectedActivityDates} />

                            {_selectedActivityDates.map((date) => (
                              <StripedTableRow>
                                <StripedTableCell>
                                  {dayjs(date.date).format("MM/DD/YY")}
                                </StripedTableCell>
                                <StripedTableCell>
                                  {getPeriodName(_school, date.period - 1)}
                                </StripedTableCell>
                                <StripedTableCell align="center">
                                  <IconButton
                                    edge="end"
                                    aria-label="delete date"
                                    onClick={() => handleClickRemoveDate(date)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </StripedTableCell>
                              </StripedTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </>
              )}

              {_activeStep === 2 && (
                <>
                  <Alert severity="info">
                    1. Check "Student cannot Modify" to disable the student from
                    being able to remove this activity.
                    <br />
                    2. Check "Another Teacher cannot Modify" to disable another
                    teacher from "stealing" this student.
                    <br />
                    3. Click the FINISH button at the bottom of the screen to
                    add all of the students to the specified activity dates.
                  </Alert>

                  <SubTitle
                    text={"Selected Activity: " + getActivityName()}
                    sx={{ mt: 2 }}
                  />

                  <InputLabel>3.1. Modify Options:</InputLabel>
                  <Table dense sx={{ mt: 0, border: 0, mb: 0 }} size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ mt: 0, pt: 0, border: 0 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={{ ml: 0, mr: 1, pl: 0, pr: 0 }}
                                onChange={(event) =>
                                  handleChangeStudentCannotModify(event)
                                }
                                checked={_studentCannotModify}
                              />
                            }
                            label="Student cannot Modify"
                          />
                        </TableCell>
                        <TableCell sx={{ mt: 0, pt: 0, border: 0 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={{ ml: 0, mr: 1, pl: 0, pr: 0 }}
                                onChange={(event) =>
                                  handleChangeTeacherCannotModify(event)
                                }
                                checked={_teacherCannotModify}
                              />
                            }
                            label="Another Teacher cannot Modify"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Table
                    dense
                    sx={{
                      mt: 0,
                      border: 0,
                      mb: 0,
                      overflow: "auto",
                      maxHeight: 300,
                    }}
                    size="small"
                  >
                    <TableHead>
                      <StripedTableRow>
                        <StripedTableCell>Student</StripedTableCell>
                        <StripedTableCell>Dates</StripedTableCell>
                      </StripedTableRow>
                    </TableHead>
                    <TableBody>
                      <TableRowNoResults data={_selectedStudents} />

                      {_selectedStudents.map((student) => (
                        <StripedTableRow>
                          <StripedTableCell width={300}>
                            {student.label}
                          </StripedTableCell>
                          <StripedTableCell>
                            {_selectedActivityDates.map((date) => (
                              <Chip
                                color="primary"
                                sx={{ mr: 1, mb: 1 }}
                                label={
                                  dayjs(date.date).format("MM/DD") +
                                  "-" +
                                  getPeriodName(_school, date.period - 1)
                                }
                              />
                            ))}
                            <TableRowNoResults data={_selectedStudents} />
                          </StripedTableCell>
                        </StripedTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Button
                variant="outlined"
                disabled={_activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Previous
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />

              {_activeStep !== totalSteps() - 1 && (
                <Button variant="contained" onClick={handleNext} sx={{ mr: 1 }}>
                  Next
                </Button>
              )}

              {_activeStep === totalSteps() - 1 && (
                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={_isFinishDisabled}
                >
                  FINISH
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      <SelectStudentsDialog
        open={_dialogStudentsOpen}
        onClose={handleStudentsClose}
        onOK={handleStudentsOK}
        students={_allStudents}
      />

      <SelectStudentListsDialog
        open={_dialogStudentListsOpen}
        onClose={handleStudentListClose}
        onOK={handleStudentListOK}
        studentLists={_userLists}
      />

      <AddStudentResultDialog
        open={_dialogStudentResultOpen}
        successes={_addSuccesses}
        fails={_addFails}
        onClose={handleStudentResultClose}
      />
    </>
  );
};

export default BulkChange;
